export const DEFAULT_DOCUMENT_TITLE = "io.net | Decentralized GPU Cloud";
export const SITE_NAME = "io.net";

type PageTitleRoute = {
  path: string;
  title: string;
};

/**
 * Most specific paths first so `/cloud/container/:id/logs` wins over `/cloud/container/:id`.
 * No React or app imports — Vercel middleware bundles this file too.
 */
export const PAGE_TITLE_ROUTES: PageTitleRoute[] = [
  { path: "/login/signup", title: "Sign up" },
  { path: "/login/:section", title: "Log in" },
  { path: "/login", title: "Log in" },

  { path: "/cloud/dashboard", title: "Dashboard" },

  { path: "/cloud/home", title: "Home" },
  { path: "/cloud/virtual-machines/create-cluster", title: "Create Virtual Machine" },
  { path: "/cloud/virtual-machines/:id", title: "VMaaS" },
  { path: "/cloud/virtual-machines", title: "Virtual Machines" },
  { path: "/cloud/container/create-cluster", title: "Create Container" },
  { path: "/cloud/container/:id/adjust", title: "Adjust CaaS" },
  { path: "/cloud/container/:id/logs", title: "CaaS Logs" },
  { path: "/cloud/container/:id", title: "CaaS" },
  { path: "/cloud/container", title: "Containers" },
  { path: "/cloud/bare-metal-old", title: "Bare Metal" },
  { path: "/cloud/bare-metal/:id", title: "Bare Metal" },
  { path: "/cloud/bare-metal", title: "Bare Metal" },
  { path: "/cloud/kubernetes/create-cluster", title: "Create Kubernetes Cluster" },
  { path: "/cloud/kubernetes/:id", title: "KaaS" },
  { path: "/cloud/kubernetes", title: "Kubernetes" },
  { path: "/cloud/confidential-compute", title: "Confidential Compute" },
  { path: "/cloud/ray/:id", title: "Ray" },
  { path: "/cloud/ray", title: "Ray" },

  { path: "/ai/app/:id", title: "Chat" },
  { path: "/ai/plans", title: "Plans" },
  { path: "/ai/models/:id/chat/:chatId", title: "Chat" },
  { path: "/ai/models/:id", title: "Model" },
  { path: "/ai/models", title: "Models" },
  { path: "/ai/api-keys", title: "API Keys and Secrets" },
  { path: "/ai/agents", title: "Agents" },
  { path: "/ai/agentic-workflow-editor/:workflowId", title: "Agentic Workflow Editor" },
  { path: "/ai/agentic-workflow-editor", title: "Agentic Workflow Editor" },
  { path: "/ai/training/create", title: "Create Training Job" },
  { path: "/ai/training/job/:jobId", title: "Training" },
  { path: "/ai/training", title: "Training" },

  { path: "/worker/dashboard/devices/create/:id", title: "Create Worker" },
  { path: "/worker/dashboard/devices/:id", title: "Worker" },
  { path: "/worker/dashboard/devices", title: "Workers" },
  { path: "/worker/dashboard/earnings-and-rewards", title: "Earnings and Rewards" },
  { path: "/worker/dashboard/staking/create-offer/:deviceId", title: "Create Co-Staking Offer" },
  { path: "/worker/dashboard/staking/devices/:section", title: "Staking" },
  { path: "/worker/dashboard/staking/devices", title: "Staking" },
  { path: "/worker/devices/explorer/inventory", title: "Inventory" },
  { path: "/worker/devices/explorer/:id", title: "Worker" },
  { path: "/worker/devices/explorer", title: "Workers" },

  { path: "/staking/co-staking-marketplace", title: "Co-Staking Marketplace" },
  { path: "/staking/my-co-staking", title: "My Co-Staking Devices" },
  { path: "/staking/safety-module", title: "IO Safety Module" },

  { path: "/explorer/home", title: "Explorer" },
  { path: "/explorer/clusters/:id", title: "Cluster" },
  { path: "/explorer/clusters", title: "Clusters" },
  { path: "/explorer/inferences/:id", title: "Inference" },
  { path: "/explorer/inferences", title: "Inferences" },
  { path: "/explorer/epoch-rewards", title: "Block Rewards" },
  { path: "/explorer/staking-dashboard", title: "Staking Dashboard" },
  { path: "/explorer/epochs/:epoch_id", title: "Epoch" },
  { path: "/explorer/epochs", title: "Epochs" },

  { path: "/id/account/overview", title: "Quick Start" },
  { path: "/id/account/io-cloud", title: "io.cloud" },
  { path: "/id/account/settings", title: "Settings" },
  { path: "/id/account/intelligence", title: "io.intelligence" },
  { path: "/id/account/acknowledgment", title: "User Acknowledgment" },
  { path: "/id/account/transactions-and-balance", title: "Transactions and Balance" },
  { path: "/id/account/security", title: "Sign In and Security" },
  { path: "/id/account/support", title: "Support" },
  { path: "/id/account/notification", title: "Notifications" },
  { path: "/id/account/io-elements", title: "Clusters" },
  { path: "/id/funds/:id", title: "Billing" },
  { path: "/id/funds", title: "Billing History" },

  { path: "/:tool/devices/:id", title: "Worker" }
];

/** Product-style prefixes for cloud cluster detail titles (e.g. `CaaS my-cluster`). */
export const CLUSTER_TYPE_TITLE_PREFIX: Record<string, string> = {
  container: "CaaS",
  vmaas: "VMaaS",
  kubernetes: "KaaS",
  "bare-metal": "Bare Metal",
  ray: "Ray",
  cluster: "Cluster",
  mega: "Mega",
  "confidential-compute": "Confidential Compute"
};

export const formatNamedPageTitle = (prefix: string, name?: string | null): string => {
  const trimmed = name?.trim();
  return trimmed ? `${prefix} ${trimmed}` : prefix;
};

const patternToRegex = (pattern: string): RegExp => {
  const parts = pattern.split("/").filter(Boolean);
  const regexParts = parts.map((part) => {
    if (part.startsWith(":")) {
      return "[^/]+";
    }
    return part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  });
  return new RegExp(`^/${regexParts.join("/")}$`);
};

const ROUTE_MATCHERS = PAGE_TITLE_ROUTES.map((route) => ({
  title: route.title,
  regex: patternToRegex(route.path)
}));

export const normalizePathname = (pathname: string): string => {
  const withoutQuery = pathname.split("?")[0].split("#")[0];
  if (withoutQuery.length > 1 && withoutQuery.endsWith("/")) {
    return withoutQuery.slice(0, -1);
  }
  return withoutQuery || "/";
};

const pathCandidates = (pathname: string): string[] => {
  const path = normalizePathname(pathname);
  const prefixed = path.match(/^\/(explorer|cloud)(\/(?:id|staking)(?:\/.*)?)$/);
  if (prefixed) {
    return [path, prefixed[2]];
  }
  return [path];
};

export const formatPageTitle = (pageName: string): string => `${pageName} | ${SITE_NAME}`;

export const getPageName = (pathname: string): string | undefined => {
  for (const candidate of pathCandidates(pathname)) {
    for (const route of ROUTE_MATCHERS) {
      if (route.regex.test(candidate)) {
        return route.title;
      }
    }
  }
  return undefined;
};

export const getPageTitle = (pathname: string): string => {
  const pageName = getPageName(pathname);
  return pageName ? formatPageTitle(pageName) : DEFAULT_DOCUMENT_TITLE;
};

const escapeHtml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export const applyPageTitleToHtml = (html: string, pathname: string): string => {
  const title = getPageTitle(pathname);
  const escaped = escapeHtml(title);

  return html
    .replace(/<title>[^<]*<\/title>/i, `<title>${escaped}</title>`)
    .replace(/(property="og:title"\s+content=")[^"]*(")/i, `$1${escaped}$2`)
    .replace(/(name="twitter:title"\s+content=")[^"]*(")/i, `$1${escaped}$2`);
};
